import React, { useContext, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { RowNode } from '@ag-grid-enterprise/all-modules';
import ConceptLinkerValidatorItemsModel from 'models/ConceptLinkerValidatorItemsModel';
import { ConceptLinkerValidatorModel } from 'models/ConceptLinkerValidatorModel';
import { useForceRender } from 'hooks/useForceRender';
import Grid from 'CMFW/grid/Grid';
import DeleteButton from 'CMFW/button/DeleteButton';
import ModuleContext from 'CMFW/context/ModuleContext';
import ValidatorHeader from 'components/ValidatorHeader';
import {
  ConceptLinkerValidatorItemStatus,
  ConceptLinkerValidatorStatus,
  AsyncTaskStatus,
} from 'constants/conceptLinkerValidatorStatus';
import { asyncTaskStatusRenderer } from '../../CMFW/grid/cellRenderers/asyncTaskStatus';
import { conceptValidatorItemStatusRenderer } from 'CMFW/grid/cellRenderers/conceptValidatorItemStatus';
import { IFields } from 'CMFW/grid/fields';
import { compareAiConfidence } from 'CMFW/grid/comparators/compareAiConfidence';
import { IConceptLinkerValidatorItem, IConceptLinkerValidatorAlternativeItem } from 'types/conceptLinkerValidator';
import ConceptLinkerValidatorSummary from './components/ConceptLinkerValidatorSummary';
import ConceptLinkerValidatorBulkActions from './components/ConceptLinkerValidatorBulkActions';
import ConceptCardHover from 'components/common/ConceptCardHover';
import PrioritySelector from 'components/PrioritySelector';

// Module-level variables for filter state
let currentFilter: { type: 'status' | 'decision' | 'change-concept' | 'cannot-link'; value: string } | null = null;

const ConceptLinkerValidatorDetails: React.FC = () => {
  const conceptLinkerValidatorModel = useContext(ModuleContext) as ConceptLinkerValidatorModel;
  const validationId = conceptLinkerValidatorModel.itemId;
  const showMockButton = false;

  // Do not early return before hooks; guard rendering later

  const forceRender = useForceRender();
  const [isProcessingMock, setIsProcessingMock] = useState(false);
  const [activeFilter, setActiveFilter] = useState<{
    type: 'status' | 'decision' | 'change-concept' | 'cannot-link';
    value: string;
  } | null>(null);
  const [jobPriority, setJobPriority] = useState<number | undefined>(undefined);

  // Update module-level variable when state changes
  currentFilter = activeFilter;

  const name = conceptLinkerValidatorModel.getItemValue('name');
  const status = conceptLinkerValidatorModel.getItemValue('status');
  const itemRef = conceptLinkerValidatorModel.getItemValue('item_ref');
  const itemRefId = conceptLinkerValidatorModel.getItemValue('item_ref_id');
  const validatorCourseId = conceptLinkerValidatorModel.getItemValue('courseId') || 0;
  const alternativeItems: IConceptLinkerValidatorAlternativeItem[] =
    conceptLinkerValidatorModel.getItemValue('alternative_items') || [];

  // Create a single items model instance
  const itemsModel = useMemo(() => new ConceptLinkerValidatorItemsModel({ render: forceRender }), [forceRender]);

  // Update model when validation changes
  useEffect(() => {
    if (validationId > 0) {
      itemsModel.setValidationId(validationId);
      // Clear filter when switching to a different validation
      setActiveFilter(null);
      // Load job priority
      conceptLinkerValidatorModel.loadItemData(validationId).then((data) => {
        if (data && data.priority !== undefined) {
          setJobPriority(data.priority);
        }
      });
    }
  }, [validationId, itemsModel, conceptLinkerValidatorModel]);

  // Force grid to re-filter when activeFilter changes
  useEffect(() => {
    // Trigger a quick loading state to force the grid to call onFilterChanged
    itemsModel.loadingGrid = true;
    itemsModel.render();
    setTimeout(() => {
      itemsModel.loadingGrid = false;
      itemsModel.render();
    }, 0);
  }, [activeFilter, itemsModel]);

  const handleMockProcess = async () => {
    if (!validationId || isProcessingMock) return;

    setIsProcessingMock(true);
    try {
      await conceptLinkerValidatorModel.processMock(validationId);
      await itemsModel.reload();
      forceRender();
    } catch (error) {
      console.error('Error during mock processing:', error);
    } finally {
      setIsProcessingMock(false);
    }
  };

  // Filter handlers
  const handleFilterChange = (type: 'status' | 'decision' | 'change-concept' | 'cannot-link', value: string) => {
    if (activeFilter?.type === type && activeFilter?.value === value) {
      // Toggle off if clicking the same filter
      setActiveFilter(null);
    } else {
      setActiveFilter({ type, value });
    }
  };

  const clearFilter = () => {
    setActiveFilter(null);
  };

  const handleChangePriority = async (newPriority: number) => {
    await conceptLinkerValidatorModel.changePriority(validationId, newPriority);
    setJobPriority(newPriority);
  };

  // Filter check functions
  const isFilterActive = () => {
    return currentFilter !== null;
  };

  const isRowVisible = (node: RowNode) => {
    if (!currentFilter) return true;

    const data: IConceptLinkerValidatorItem = node.data;
    if (!data) return true;

    switch (currentFilter.type) {
      case 'status':
        if (currentFilter.value === 'processing') {
          return data.status === ConceptLinkerValidatorItemStatus.PENDING;
        } else if (currentFilter.value === 'review') {
          return data.status === ConceptLinkerValidatorItemStatus.NEEDS_USER_REVIEW;
        } else if (currentFilter.value === 'error') {
          return (
            data.ai_result?.decision === 'error' ||
            data.async_task_status === AsyncTaskStatus.FAILED ||
            data.async_task_status === AsyncTaskStatus.DEAD_LETTER
          );
        }
        break;

      case 'decision':
        // Show items that match the decision filter:
        // 1. Completed items with this final decision
        // 2. Items in review with this AI suggestion
        if (data.status === ConceptLinkerValidatorItemStatus.DONE) {
          return data.decision === currentFilter.value;
        } else if (data.status === ConceptLinkerValidatorItemStatus.NEEDS_USER_REVIEW) {
          return data.ai_result?.decision === currentFilter.value;
        }
        break;

      case 'change-concept':
        // Filter by specific change concept
        const [ref, refId] = currentFilter.value.split('_');
        if (
          data.decision === 'change' ||
          (data.ai_result?.decision === 'change' && data.status !== ConceptLinkerValidatorItemStatus.DONE)
        ) {
          return data.ai_result?.selected_ref === parseInt(ref) && data.ai_result?.selected_refId === parseInt(refId);
        }
        break;

      case 'cannot-link':
        // Show items that have unlinkable concepts
        return !!(data.ai_result?.unlinkable_concepts && data.ai_result.unlinkable_concepts.length > 0);
    }

    return false;
  };

  // Create cell renderers
  const cellRenderers = useMemo(
    () => ({
      [IFields.conceptValidatorActions]: 'conceptValidatorActionRenderer',
      [IFields.moduleNavigation]: 'moduleNavigationRenderer',
      [IFields.conceptValidatorAiStatus]: asyncTaskStatusRenderer,
      [IFields.conceptValidatorAiResult]: 'conceptValidatorAiResultRenderer',
      [IFields.conceptValidatorItemStatus]: conceptValidatorItemStatusRenderer,
      [IFields.agent_prompt_log]: 'agentPromptLogRenderer',
    }),
    [],
  );

  const columnSorters = useMemo(() => ({ [IFields.conceptValidatorAiResult]: compareAiConfidence }), []);

  // Grid options for row styling
  const gridOptions = useMemo(
    () => ({
      getRowStyle: (params: any) => {
        if (params.data) {
          // Check for error state first
          if (
            (params.data.ai_result && params.data.ai_result.decision === 'error') ||
            params.data.async_task_status === AsyncTaskStatus.FAILED ||
            params.data.async_task_status === AsyncTaskStatus.DEAD_LETTER
          ) {
            return { backgroundColor: '#ffebee' }; // Very light red for errors
          }

          // Then check status
          if (typeof params.data.status === 'number') {
            // Highlight pending items with very light yellow background
            if (params.data.status === ConceptLinkerValidatorItemStatus.PENDING) {
              return { backgroundColor: '#fffef7' };
            }
            // Highlight items needing review with very light orange
            if (params.data.status === ConceptLinkerValidatorItemStatus.NEEDS_USER_REVIEW) {
              return { backgroundColor: '#fff8f0' };
            }
          }
        }
        return undefined;
      },
    }),
    [],
  );

  // Guard render after establishing hooks to satisfy rules-of-hooks
  if (!validationId || validationId === -1) {
    return null;
  }

  return (
    <Container>
      <ValidatorHeader
        id={validationId}
        name={name}
        idLabel="Validation"
        customTitle={
          <TitleSection>
            <span>{name}</span>
            {itemRef && itemRefId && (
              <ConceptInfo>
                <Separator>-</Separator>
                <ConceptCardHover
                  conceptRef={itemRef}
                  refId={itemRefId}
                  courseId={validatorCourseId}
                  showLabel={true}
                />
                {alternativeItems && alternativeItems.length > 0 && (
                  <>
                    <Arrow>→</Arrow>
                    {alternativeItems.map((alt, index) => (
                      <React.Fragment key={`${alt.ref}_${alt.refId}`}>
                        {index > 0 && <AltSeparator>,</AltSeparator>}
                        <ConceptCardHover
                          conceptRef={alt.ref}
                          refId={alt.refId}
                          courseId={validatorCourseId}
                          showLabel={true}
                        />
                      </React.Fragment>
                    ))}
                  </>
                )}
              </ConceptInfo>
            )}
          </TitleSection>
        }
        bulkActions={
          <>
            {showMockButton && status === ConceptLinkerValidatorStatus.PENDING && (
              <MockProcessButton onClick={handleMockProcess} disabled={isProcessingMock}>
                {isProcessingMock ? 'Processing...' : 'Mock AI Process'}
              </MockProcessButton>
            )}
            <ConceptLinkerValidatorBulkActions
              itemsModel={itemsModel}
              conceptLinkerValidatorModel={conceptLinkerValidatorModel}
              onActionComplete={forceRender}
            />
            <PrioritySelector
              currentPriority={jobPriority ?? 1}
              onChangePriority={handleChangePriority}
              isProcessing={itemsModel.gridData?.some((item: any) => item.status === ConceptLinkerValidatorItemStatus.PENDING) ?? false}
            />
          </>
        }
        itemsModel={itemsModel}
      />

      <ConceptLinkerValidatorSummary
        itemsModel={itemsModel}
        onFilterChange={handleFilterChange}
        activeFilter={activeFilter}
        onClearFilter={clearFilter}
        courseId={validatorCourseId}
      />

      <GridContainer>
        <ModuleContext.Provider value={itemsModel}>
          <Grid
            cellRenderers={cellRenderers}
            gridOptions={gridOptions}
            isFilterActive={isFilterActive}
            isRowVisible={isRowVisible}
            columnSorters={columnSorters}
          />
        </ModuleContext.Provider>
      </GridContainer>

      <DeleteButtonContainer>
        <ModuleContext.Provider value={conceptLinkerValidatorModel}>
          <DeleteButton />
        </ModuleContext.Provider>
      </DeleteButtonContainer>
    </Container>
  );
};

export default ConceptLinkerValidatorDetails;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const TitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ConceptInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
`;

const Separator = styled.span`
  color: #999;
  font-size: 20px;
`;

const Arrow = styled.span`
  color: #666;
  font-size: 18px;
  margin: 0 8px;
`;

const AltSeparator = styled.span`
  color: #999;
  font-size: 16px;
  margin: 0 4px;
`;

const MockProcessButton = styled.button`
  padding: 8px 16px;
  background-color: #2196f3;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.3s;

  &:hover:not(:disabled) {
    background-color: #1976d2;
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const GridContainer = styled.div`
  flex: 1;
  overflow: auto;
  position: relative;
`;

const DeleteButtonContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  padding-top: 4px;
`;
